// src/contexts/GlobalContext.tsx
import { IERPUser } from '@admin-panel/types';
import { IOrganization } from '@admin-panel/types/lib/org';
import { useGetIdentity } from '@refinedev/core';
import { createContext, PropsWithChildren, useContext, useEffect, useState } from 'react';

import { NoNetworkShell, ShellLoading, useOrganization } from './organization';

export { recordSwitchedTo, switchFacilityForRecord } from './recordSwitch';
export { NoNetworkShell, noNetworkShellText, ORG_CACHE_KEY, useOrganization } from './organization';

export type DesktopFilterLayout = 'horizontal' | 'vertical';

export const SELECTED_WAREHOUSE = 'selectedWHId';
export const ALL_FACILITIES_KEY = 'ALL_FACILITIES';
const DESKTOP_FILTER_LAYOUT_KEY = 'discovery.desktop.layout';
const DEFAULT_DESKTOP_LAYOUT: DesktopFilterLayout = 'horizontal';

interface GlobalContextType {
  organization: IOrganization;
  warehouseId?: number | typeof ALL_FACILITIES_KEY;
  warehouseIds?: number[];
  isAllFacilities: boolean;
  /** False until a facility is saved for this browser, or the user has only one: the phone asks first. */
  facilityChosen: boolean;
  setWarehouseId: (warehouseId: number | typeof ALL_FACILITIES_KEY) => void;
  setAllFacilities: (facilityIds: number[]) => void;
  desktopFilterLayout: DesktopFilterLayout;
  setDesktopFilterLayout: (layout: DesktopFilterLayout) => void;
}

const GlobalContext = createContext<GlobalContextType | undefined>(undefined);

type GlobalProviderProps = PropsWithChildren<{}>;

const isSuperAdmin = (user: IERPUser) => {
  const index = user?.roles.findIndex(r => r.name == 'SUPER_ADMIN');
  if (index != -1) return true;
  return false;
};

const isWarehouseAdmin = (user: IERPUser) => {
  const index = user?.roles.findIndex(r => r.name == 'WAREHOUSE_ADMIN');
  if (index != -1) return true;
  return false;
};

const hasAllFacilitiesRole = (user: IERPUser) => {
  const index = user?.roles.findIndex(r => r.name == 'ALL_FACILITIES');
  if (index != -1) return true;
  return false;
};

const canAccessAllFacilities = (user: IERPUser | undefined) => {
  if (!user) return false;
  return isSuperAdmin(user) || isWarehouseAdmin(user) || hasAllFacilitiesRole(user);
};

export const GlobalProvider = ({ children }: GlobalProviderProps) => {
  const { data: user } = useGetIdentity<IERPUser>();

  const [warehouseId, setWarehouseId] = useState<number | typeof ALL_FACILITIES_KEY | undefined>();
  const [warehouseIds, setWarehouseIds] = useState<number[] | undefined>();
  const [isAllFacilities, setIsAllFacilities] = useState<boolean>(false);
  const [facilityChosen, setFacilityChosen] = useState<boolean>(false);
  const [desktopFilterLayout, setDesktopFilterLayoutState] =
    useState<DesktopFilterLayout>(DEFAULT_DESKTOP_LAYOUT);

  useEffect(() => {
    if (
      user?.adminUserAttributes?.warehouseIds &&
      user?.adminUserAttributes?.warehouseIds.length > 0
    ) {
      const savedWHId = localStorage.getItem(SELECTED_WAREHOUSE);
      if (savedWHId === ALL_FACILITIES_KEY && canAccessAllFacilities(user)) {
        setWarehouseId(ALL_FACILITIES_KEY);
        setWarehouseIds(user?.adminUserAttributes?.warehouseIds);
        setIsAllFacilities(true);
        setFacilityChosen(true);
      } else if (
        savedWHId &&
        user?.adminUserAttributes?.warehouseIds.includes(parseInt(savedWHId))
      ) {
        setWarehouseId(parseInt(savedWHId));
        setWarehouseIds([parseInt(savedWHId)]);
        setIsAllFacilities(false);
        setFacilityChosen(true);
      } else {
        setWarehouseId(user?.adminUserAttributes?.warehouseIds[0]);
        setWarehouseIds([user?.adminUserAttributes?.warehouseIds[0]]);
        setIsAllFacilities(false);
        setFacilityChosen(user?.adminUserAttributes?.warehouseIds.length === 1);
      }
    }
  }, [user]);

  useEffect(() => {
    const savedLayout = localStorage.getItem(DESKTOP_FILTER_LAYOUT_KEY);
    if (savedLayout === 'horizontal' || savedLayout === 'vertical') {
      setDesktopFilterLayoutState(savedLayout);
    } else {
      localStorage.setItem(DESKTOP_FILTER_LAYOUT_KEY, DEFAULT_DESKTOP_LAYOUT);
    }
  }, []);

  // Kept from the last read: a reload with no signal still opens on the last session (round 14).
  const { organization, loading: orgLoading, retrying, retry } = useOrganization();

  const updateWarehouseId = (value: number | typeof ALL_FACILITIES_KEY) => {
    if (value === ALL_FACILITIES_KEY && canAccessAllFacilities(user)) {
      localStorage.setItem(SELECTED_WAREHOUSE, ALL_FACILITIES_KEY);
      setWarehouseId(ALL_FACILITIES_KEY);
      setWarehouseIds(user?.adminUserAttributes?.warehouseIds || []);
      setIsAllFacilities(true);
      setFacilityChosen(true);
    } else if (value === ALL_FACILITIES_KEY && !canAccessAllFacilities(user)) {
      // If user doesn't have permission, don't allow All Facilities selection
      return;
    } else {
      localStorage.setItem(SELECTED_WAREHOUSE, value.toString());
      setWarehouseId(value);
      setWarehouseIds([value as number]);
      setIsAllFacilities(false);
      setFacilityChosen(true);
    }
  };

  const handleSetAllFacilities = (facilityIds: number[]) => {
    if (!canAccessAllFacilities(user)) {
      return;
    }
    setWarehouseIds(facilityIds);
    setIsAllFacilities(true);
  };

  if (orgLoading) return <ShellLoading />;

  // A failed read is the network, never a missing permission (round 14: "You are not allowerd" on a reload offline).
  if (!organization) return <NoNetworkShell retrying={retrying} onRetry={retry} />;

  const updateDesktopFilterLayout = (layout: DesktopFilterLayout) => {
    localStorage.setItem(DESKTOP_FILTER_LAYOUT_KEY, layout);
    setDesktopFilterLayoutState(layout);
  };

  return (
    <GlobalContext.Provider
      value={{
        organization,
        warehouseId: warehouseId,
        warehouseIds: warehouseIds,
        isAllFacilities: isAllFacilities,
        facilityChosen,
        setWarehouseId: (whId: number | typeof ALL_FACILITIES_KEY) => updateWarehouseId(whId),
        setAllFacilities: handleSetAllFacilities,
        desktopFilterLayout,
        setDesktopFilterLayout: updateDesktopFilterLayout,
      }}
    >
      {children}
    </GlobalContext.Provider>
  );
};

export const useGlobalContext = () => {
  const context = useContext(GlobalContext);
  if (context === undefined) {
    throw new Error('useGlobalContext must be used within a GlobalProvider');
  }
  return context;
};
